import { useParams, Link } from "react-router-dom";
import { comics } from "@/mocks/comics";
import ComicCard from "@/components/base/ComicCard";
import Breadcrumb from "@/components/base/Breadcrumb";

export default function Article() {
  const { id } = useParams<{ id: string }>();
  const comic = comics.find((c) => c.id === id);

  if (!comic) {
    return (
      <main className="min-h-screen bg-background-50 pt-16 flex items-center justify-center">
        <div className="text-center">
          <div className="w-20 h-20 mx-auto flex items-center justify-center bg-background-100 rounded-full mb-4">
            <i className="ri-file-unknow-line text-4xl text-foreground-400"></i>
          </div>
          <h1 className="text-xl font-heading font-bold text-foreground-900 mb-2">漫画未找到</h1>
          <p className="text-foreground-500 mb-4">抱歉，您要查看的漫画不存在或已下架</p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors duration-200 whitespace-nowrap"
          >
            <i className="ri-arrow-left-line"></i> 返回首页
          </Link>
        </div>
      </main>
    );
  }

  const relatedComics = comics
    .filter((c) => c.category === comic.category && c.id !== comic.id)
    .slice(0, 4);

  const categoryName = (() => {
    const cats: Record<string, string> = {
      romance: "恋爱", fantasy: "奇幻", action: "动作", suspense: "悬疑",
      comedy: "搞笑", horror: "恐怖", school: "校园", urban: "都市",
    };
    return cats[comic.category] || comic.category;
  })();

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: `${comic.title} - 韩漫漫画网免费在线阅读`,
    description: comic.description,
    author: { "@type": "Person", name: comic.author },
    datePublished: comic.chapters[0]?.updatedAt || comic.updatedAt,
    dateModified: comic.updatedAt,
    publisher: {
      "@type": "Organization",
      name: "韩漫漫画网",
    },
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
      <main className="min-h-screen bg-background-50 pt-16">
        <Breadcrumb
          items={[
            { label: "首页", href: "/" },
            { label: "分类浏览", href: "/category/all" },
            { label: categoryName, href: `/category/${comic.category}` },
            { label: comic.title },
          ]}
        />

        <article className="max-w-7xl mx-auto px-4 md:px-6 py-6">
          <div className="flex flex-col lg:flex-row gap-8">
            <div className="flex-shrink-0">
              <div className="w-48 md:w-56 aspect-[3/4] rounded-lg overflow-hidden">
                <img
                  src={comic.cover}
                  alt={`${comic.title} 封面`}
                  title={`${comic.title} - 韩漫漫画网免费在线阅读`}
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>

            <div className="flex-1">
              <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
                {comic.title}
              </h1>

              <div className="flex items-center flex-wrap gap-2 mb-4">
                {comic.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-2.5 py-0.5 rounded-full bg-accent-100 text-accent-800 text-xs font-medium"
                  >
                    {tag}
                  </span>
                ))}
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-6">
                <div>
                  <p className="text-xs text-foreground-400">作者</p>
                  <p className="text-sm font-medium text-foreground-900">{comic.author}</p>
                </div>
                <div>
                  <p className="text-xs text-foreground-400">状态</p>
                  <p className="text-sm font-medium">
                    <span className={comic.status === "连载中" ? "text-primary-500" : "text-secondary-500"}>
                      {comic.status}
                    </span>
                  </p>
                </div>
                <div>
                  <p className="text-xs text-foreground-400">评分</p>
                  <div className="flex items-center gap-1">
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-star-fill text-accent-500 text-sm"></i>
                    </div>
                    <span className="text-sm font-medium text-foreground-900">{comic.rating}</span>
                  </div>
                </div>
                <div>
                  <p className="text-xs text-foreground-400">总章节</p>
                  <p className="text-sm font-medium text-foreground-900">{comic.totalChapters}话</p>
                </div>
                <div>
                  <p className="text-xs text-foreground-400">阅读量</p>
                  <p className="text-sm font-medium text-foreground-900">
                    {comic.views >= 10000 ? `${(comic.views / 10000).toFixed(1)}万` : comic.views}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-foreground-400">最近更新</p>
                  <p className="text-sm font-medium text-foreground-900">{comic.updatedAt}</p>
                </div>
              </div>

              <div className="mb-6">
                <h2 className="font-heading text-base font-semibold text-foreground-900 mb-2">作品简介</h2>
                <p className="text-sm text-foreground-600 leading-relaxed">{comic.fullDescription}</p>
              </div>

              <div className="flex flex-wrap gap-3">
                <button className="px-6 py-2.5 bg-primary-500 hover:bg-primary-600 text-background-50 rounded-full text-sm font-medium transition-colors duration-200 whitespace-nowrap cursor-pointer">
                  开始阅读
                </button>
                <button className="px-6 py-2.5 bg-secondary-100 hover:bg-secondary-200 text-secondary-800 rounded-full text-sm font-medium transition-colors duration-200 whitespace-nowrap cursor-pointer">
                  <div className="w-4 h-4 inline-flex items-center justify-center mr-1">
                    <i className="ri-bookmark-line text-sm"></i>
                  </div>
                  加入收藏
                </button>
              </div>
            </div>
          </div>

          <section className="mt-10" aria-label="章节列表">
            <h2 className="font-heading text-lg font-bold text-foreground-900 mb-4">章节列表</h2>
            <div className="bg-background-100 rounded-lg divide-y divide-background-200">
              {comic.chapters.map((chapter) => (
                <div
                  key={chapter.id}
                  className="flex items-center justify-between px-5 py-3.5 hover:bg-background-200/50 transition-colors duration-150 cursor-pointer"
                >
                  <div className="flex items-center gap-3">
                    <span className="text-xs text-foreground-500 font-mono">第{chapter.number}话</span>
                    <span className="text-sm text-foreground-900">{chapter.title}</span>
                  </div>
                  <span className="text-xs text-foreground-400">{chapter.updatedAt}</span>
                </div>
              ))}
            </div>
            {comic.totalChapters > comic.chapters.length && (
              <div className="text-center mt-4">
                <button className="text-sm text-primary-500 hover:text-primary-600 transition-colors duration-200 cursor-pointer">
                  查看更多章节 ({comic.totalChapters}话)
                </button>
              </div>
            )}
          </section>

          {relatedComics.length > 0 && (
            <section className="mt-10" aria-label="相关推荐">
              <h2 className="font-heading text-lg font-bold text-foreground-900 mb-4">相关推荐</h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4" data-product-shop>
                {relatedComics.map((c) => (
                  <ComicCard key={c.id} comic={c} />
                ))}
              </div>
            </section>
          )}
        </article>
      </main>
    </>
  );
}